'use client';

import { ReactNode, useState } from 'react';

import { cn } from 'cn';

import { getExternalEmbedSandbox } from '@/shared-utilities';

import { useMobileAppClient } from './shared-mobile-app-client.hooks';
import MobileAppFrameAlert from './shared-mobile-app-frame-alert';
import {
  MOBILE_APP_DEBUG_MAX_LOG_ENTRIES,
  MOBILE_APP_IFRAME_ID,
} from './shared-mobile-app.consts';
import type {
  MobileAppDebugLogEntry,
  MobileAppDebugLogLevel,
  MobileAppDebugLogSource,
  MobileAppDebugPanelProps,
} from './shared-mobile-app.types';

interface Props {
  embedUrl: string;
  language?: string | null;
  containerClassName?: string;
  isConversationOver?: boolean;
  debugEnabled?: boolean;
  children?: ReactNode;
  isHidden?: boolean;
  renderDebugPanel?: (props: MobileAppDebugPanelProps) => ReactNode;
}

function extractBuildIdFromEmbedUrl(embedUrl: string): string | null {
  const match = embedUrl.match(/\/embed\/([^/?#]+)/);
  return match?.[1] ?? null;
}

export default function MobileAppFrame({
  embedUrl,
  language,
  containerClassName,
  isConversationOver = false,
  debugEnabled = false,
  children,
  isHidden = false,
  renderDebugPanel,
}: Props) {
  const [debugEntries, setDebugEntries] = useState<MobileAppDebugLogEntry[]>(
    []
  );
  const [isDisconnected, setIsDisconnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);

  const buildId = extractBuildIdFromEmbedUrl(embedUrl);

  function appendDebug(
    source: MobileAppDebugLogSource,
    level: MobileAppDebugLogLevel,
    label: string,
    detail?: unknown
  ) {
    if (!debugEnabled) return;
    setDebugEntries((prev) => {
      const next: MobileAppDebugLogEntry = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        timestamp: Date.now(),
        level,
        source,
        label,
        detail,
      };
      return [next, ...prev].slice(0, MOBILE_APP_DEBUG_MAX_LOG_ENTRIES);
    });
  }

  function handleClientEvent(
    source: 'sdk' | 'client' | 'session',
    label: string,
    detail?: unknown
  ) {
    appendDebug(source, 'event', label, detail);
  }

  function handleSessionEnded() {
    if (isConversationOver) return;
    appendDebug('session', 'warn', 'Appetize session ended — needs reconnect');
    setIsDisconnected(true);
  }

  const config = { buildId: buildId ?? '', language: language ?? undefined };

  const { sdkStatus, client, session, phase, queue, embedSrc, attempt, retry } =
    useMobileAppClient({
      iframeId: MOBILE_APP_IFRAME_ID,
      embedUrl,
      config,
      enabled: !isConversationOver && Boolean(buildId),
      onEvent: handleClientEvent,
      onSessionEnded: handleSessionEnded,
    });

  const isSdkPending = sdkStatus === 'idle' || sdkStatus === 'loading';

  async function handleReconnect() {
    if (!client) return;
    setReconnecting(true);
    try {
      await client.startSession(config);
      setIsDisconnected(false);
    } catch (err: unknown) {
      appendDebug('client', 'error', 'Reconnect failed', {
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setReconnecting(false);
    }
  }

  const queueText = queue
    ? `${queue.type} #${queue.position}${queue.name ? ` (${queue.name})` : ''}`
    : null;

  return (
    <div
      data-test='usability-response-mobile-app-iframe-container'
      data-hidden={isHidden}
      aria-hidden={isHidden || undefined}
      className={cn(
        'relative isolate flex w-full flex-col items-center justify-center gap-4 overflow-hidden rounded-4xl bg-slate-900',
        containerClassName,
        isHidden &&
          'pointer-events-none fixed top-0 left-[-200vw] h-screen w-screen opacity-0'
      )}
    >
      {buildId ? (
        <iframe
          key={attempt}
          id={MOBILE_APP_IFRAME_ID}
          data-test='mobile-app-iframe'
          src={embedSrc}
          title='Mobile app under test'
          className='size-full border-0'
          allow='autoplay; fullscreen'
          sandbox={getExternalEmbedSandbox(embedSrc)}
        />
      ) : (
        <div
          role='alert'
          className='absolute inset-0 z-10 flex items-center justify-center bg-slate-900 px-4 text-center text-sm text-red-200'
        >
          Invalid mobile app embed URL.
        </div>
      )}

      {!isHidden && isSdkPending && phase !== 'active' && !isDisconnected ? (
        <div
          data-test='mobile-app-iframe-loading'
          className='absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-slate-900/80'
        >
          <div className='size-8 animate-spin rounded-full border-2 border-white/20 border-t-white' />
          <p className='text-xs text-white/60'>
            {queueText
              ? `Waiting in queue (${queueText})…`
              : 'Loading mobile app…'}
          </p>
        </div>
      ) : null}

      {!isHidden && isDisconnected && !isConversationOver ? (
        <MobileAppFrameAlert
          dataTest='mobile-app-iframe-disconnected'
          message='Session disconnected. Reconnect to continue using the app.'
          actionLabel={reconnecting ? 'Reconnecting…' : 'Reconnect'}
          actionDataTest='mobile-app-reconnect-button'
          onAction={handleReconnect}
          actionDisabled={reconnecting || !client}
        />
      ) : null}

      {!isHidden && sdkStatus === 'error' && !isConversationOver ? (
        <MobileAppFrameAlert
          dataTest='mobile-app-iframe-error'
          message="We couldn't load the mobile app."
          actionLabel='Retry'
          actionDataTest='mobile-app-retry-button'
          onAction={retry}
        />
      ) : null}

      {children}

      {debugEnabled && renderDebugPanel
        ? renderDebugPanel({
            entries: debugEntries,
            onClear: () => setDebugEntries([]),
            sdkStatus,
            sessionPhase: phase,
            queueText,
            buildId: buildId ?? '',
            micStatus: '—',
            micLevel: 0,
            session,
            onLog: (source, label, detail) =>
              appendDebug(source, 'info', label, detail),
          })
        : null}
    </div>
  );
}
